import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { BOARD_KEYS } from '@/features/boards/public';
import { toApplyRequest } from '../adapters/proposal.adapter';
import { aiService } from '../api/ai.service';
import type { ApiApplyRequest } from '../api/ai.contract';
import type { ApplyResult, QuickTaskProposal } from '../types';
import type { QuickTaskDraftFormValues } from '../schemas';

export interface ApplyQuickTaskVariables {
  draft: QuickTaskDraftFormValues;
  /** The rendered proposal, used to derive isNew-driven invalidation (D7). */
  proposal: QuickTaskProposal;
  isEdited: boolean;
}

const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Apply mutation for the AI quick-create flow.
 * Invalidation on settle (D2/D7): the tasks root always; the board statuses
 * key when the proposal marked the status as new; the project tags prefix
 * when a tag was new. No optimistic insert (D7). orgId comes from the store.
 */
export function useApplyQuickTask(projectId: string, boardId: string) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const queryClient = useQueryClient();

  return useMutation<ApplyResult, unknown, ApplyQuickTaskVariables>({
    mutationFn: ({ draft, proposal, isEdited }) => {
      const request: ApiApplyRequest = toApplyRequest(draft, proposal, timeZoneId, isEdited);
      return aiService.applyQuickTask(projectId, boardId, request);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', orgId, projectId] });
      if (variables.proposal.fields.statusId?.isNew) {
        queryClient.invalidateQueries({ queryKey: BOARD_KEYS.statuses(orgId, boardId) });
      }
      if (variables.proposal.fields.groupId?.isNew) {
        queryClient.invalidateQueries({ queryKey: BOARD_KEYS.groups(orgId, boardId) });
      }
      if (variables.proposal.fields.tags?.isNew) {
        queryClient.invalidateQueries({ queryKey: ['tags', orgId, projectId] });
      }
    },
  });
}
